import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

// Styles pour les éléments de BD
const titleStyle: React.CSSProperties = {
  fontSize: '3em',
  fontWeight: 'bold',
  color: '#E62429', // Couleur rouge Marvel
  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
  textAlign: 'center',
  marginBottom: '20px',
  fontFamily: 'Impact, Charcoal, sans-serif', // Police de secours BOLD
};

const panelStyle: React.CSSProperties = {
  background: '#fff', // Fond blanc pour le panneau
  border: '2px solid #000', // Bordure noire épaisse
  borderRadius: '0px', // Coins carrés
  padding: '15px',
  // marginBottom: '0px', // Supprimé, géré par la grille 'gap'
  boxShadow: 'none', // Pas d'ombre
  color: '#000', // Texte noir par défaut
  fontFamily: '"Comic Sans MS", cursive, sans-serif', // Police typique des comics
  display: 'flex', // Utilisation de flexbox pour la disposition interne
  flexDirection: 'column', // Contenu empilé verticalement
  justifyContent: 'space-between', // Espace le contenu
  height: '100%', // Pour que les panneaux remplissent la cellule de la grille
};

const narrativeBoxStyle: React.CSSProperties = {
  backgroundColor: '#f0f0f0', // Fond légèrement gris pour distinguer
  border: '1px solid #000', // Bordure noire fine
  padding: '10px',
  marginBottom: '15px',
  fontStyle: 'italic',
  color: '#000',
  fontFamily: '"Comic Sans MS", cursive, sans-serif',
};

const speechBubbleStyle: React.CSSProperties = {
  position: 'relative',
  background: '#fff',
  border: '2px solid #000',
  borderRadius: '10px',
  padding: '10px',
  // marginBottom: '10px', // Espacement géré par flexbox parent
  maxWidth: '90%', // Légèrement plus large
  alignSelf: 'center', // Centrer la bulle si seule
  color: '#000',
  fontFamily: '"Comic Sans MS", cursive, sans-serif',
  marginTop: 'auto', // Pousse la bulle vers le bas si possible
};

// Les styles bubbleTailStyle et bubbleTailInnerStyle sont supprimés car non utilisables directement
// avec les styles inline React pour les pseudo-éléments ::before / ::after.
// Une implémentation CSS classique serait nécessaire pour les queues de bulles.

const SwissAvengersSaga = () => {
  return (
    // Fond principal de la page - gris clair simulant papier
    <div className="min-h-screen bg-gray-200 text-[#333333] py-20 pt-32">
      <Helmet>
        <title>La Saga des Swiss Avengers | Infinity Cloud</title>
        <meta name="description" content="Découvrez l'histoire et la philosophie des Swiss Avengers, l'alliance pour une IA éthique et souveraine." />
      </Helmet>

      <div className="container mx-auto px-4 max-w-5xl"> {/* Légèrement plus large */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 uppercase tracking-wider"
              style={titleStyle}>
             La Saga des Swiss Avengers
          </h1>
        </motion.div>

        {/* --- DÉBUT DE LA PLANCHE DE BD --- */}
        {/* Conteneur de la grille avec fond noir pour les gouttières */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-black p-4 rounded-lg shadow-lg">

          {/* CASE 1: Introduction Narrative */}
          <motion.div
            style={panelStyle}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <div style={narrativeBoxStyle}>
              GENÈVE, 2025. Dans un monde où l'Intelligence Artificielle étend son emprise,
              une question cruciale émerge : Qui contrôle la machine ? Les géants du cloud
              promettent puissance, mais souvent au prix de la souveraineté et de l'éthique...
            </div>
            {/* Espace réservé pour s'assurer que le texte du bas est en bas */}
            <div style={{ flexGrow: 1 }}></div>
            <p className="text-sm text-gray-600 text-center italic mt-2">Une menace plane sur l'indépendance numérique...</p>
          </motion.div>

          {/* CASE 2: Présentation de Sentinelle */}
          <motion.div
             style={panelStyle}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.4 }}
          >
            <div style={narrativeBoxStyle}>
              Mais au cœur de l'Europe, une alliance se forme. Un leader visionnaire se dresse,
              armé d'une conviction : la technologie la plus avancée doit rester ancrée dans nos valeurs.
              Voici SENTINELLE !
            </div>
            <div className="flex justify-center my-4">
              {/* Image ajustée pour mieux s'intégrer */}
              <img src="/images/swiss-avenger-sentinel.png" alt="Sentinelle" className="max-w-xs md:max-w-sm rounded-md border border-gray-400"/>
            </div>
            {/* Bulle de dialogue */}
            <div style={speechBubbleStyle}>
               Notre force réside dans notre indépendance. Grâce au RAID 0 Neuronal,
               nous bâtissons une IA puissante, locale et incorruptible !
            </div>
          </motion.div>

          {/* CASE 3: Focus sur la Technologie */}
          <motion.div
            style={panelStyle}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.4 }}
          >
            <div style={narrativeBoxStyle}>
               Son arme secrète ? Une infrastructure révolutionnaire défiant les conventions :
               le RAID 0 Neuronal. Une fusion de puissance Apple Silicon, orchestrée localement,
               garantissant vitesse et sécurité sans précédent.
            </div>
             <div className="bg-gray-100 border border-gray-400 h-40 my-4 flex items-center justify-center rounded text-center p-2">
                <p className="text-gray-600 italic text-sm">[Schéma High-Tech stylisé du RAID 0 Neuronal : Cluster Mac Ultra interconnectés localement]</p>
             </div>
             {/* Espace réservé */}
             <div style={{ flexGrow: 1 }}></div>
            <p className="text-sm text-gray-600 text-center italic mt-2">La souveraineté numérique a son champion.</p>
          </motion.div>

           {/* CASE 4: Placeholder pour la suite */}
           {/* Ce panneau prendra toute la largeur sur petits écrans, et une cellule sur md+ */}
           <motion.div
            style={{ ...panelStyle, background: '#e0e0e0' }} // Fond légèrement différent pour le placeholder
            className="md:col-span-1" // S'étend sur une colonne sur md+
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8, duration: 0.4 }}
          >
             <div className="text-center flex flex-col justify-center items-center h-full">
                <p className="text-xl font-bold text-gray-800 mb-4">[À SUIVRE...]</p>
                <p className="text-gray-600">Découvrez bientôt NovaForge et CybETH Prowess dans les prochains numéros !</p>
            </div>
          </motion.div>

        </div>
        {/* --- FIN DE LA PLANCHE DE BD --- */}

      </div>
    </div>
  );
};

export default SwissAvengersSaga;

// Note: L'ajout d'une vraie police de caractères BD (ex: Bangers, Komika) via Google Fonts ou localement
// améliorerait grandement l'aspect "comic". 